import { isSearchTooLong } from "@/helpers/searchLimit";
import { ref, onBeforeUnmount, type Ref, watch } from "vue";

type SearchCallback = (searchTerm: string) => void;

interface UseSearchQueryOptions {
	debounceMs?: number;
	searchCallback: SearchCallback;
}

export function useSearchQuery({ debounceMs = 500, searchCallback }: UseSearchQueryOptions) {
	const searchTerm = ref("");
	const inputChanged = ref(false);

	let timerId: ReturnType<typeof setTimeout> | null = null;

	const clearTimer = () => {
		if (timerId) {
			clearTimeout(timerId);
			timerId = null;
		}
	};

	// Vuetify's clearable inputs write null, so the silent-sync marker must be outside the
	// search-value domain. A value sentinel would otherwise treat a real user clear as silent.
	const noSilentValue = Symbol("no-silent-value");
	let silentValue: string | typeof noSilentValue = noSilentValue;

	watch(searchTerm, (newValue) => {
		if (silentValue !== noSilentValue && silentValue === newValue) {
			silentValue = noSilentValue;
			return;
		}
		silentValue = noSilentValue;

		clearTimer();
		if (isSearchTooLong(newValue)) return;
		timerId = setTimeout(() => {
			searchCallback(newValue?.trim() || "");
		}, debounceMs);
	});

	const search = (value: string) => {
		// Update the search term which triggers the watcher
		searchTerm.value = value || "";
	};

	const setValueSilent = (value: string) => {
		if (searchTerm.value === value) return;
		// LSP-17781: an external sync (e.g. the parent resetting scope while a debounce from
		// earlier real typing is still pending) must cancel that stale timer — otherwise it
		// fires later and replays the old typed value as if the user just typed it now.
		clearTimer();
		silentValue = value;
		searchTerm.value = value;
	};

	onBeforeUnmount(clearTimer);

	return {
		searchTerm,
		inputChanged,
		search,
		setValueSilent,
		// optionally expose if you ever need to cancel pending debounce
		cancel: clearTimer
	};
}
